/**
 * The purchase-order editor's form state (z8nrz7f7dz) and its conversion to
 * and from the API shapes. Pure, so the conversions are tested here rather
 * than in a component.
 *
 * Every numeric field is held as the string the input shows: a number input
 * mid-typing ("1.", "") is not a number yet, and coercing on each keystroke
 * would eat the decimal point.
 */

import { computePoTotals, type PoTotals } from './poTotals'
import type {
  CreatePurchaseOrderPayload,
  PoCustomFieldDef,
  PoCustomFieldValues,
  PurchaseOrderAddress,
  PurchaseOrderDetail,
  PurchaseOrderLineInput,
  PurchaseOrderPrefill,
} from '@/types/purchaseOrders'

export interface PoDraftLine {
  /** Client-side row key; lines have no id until saved. */
  key: string
  materialId: string
  articleNo: string
  supplierRef: string
  description: string
  qty: string
  uom: string
  unitPrice: string
  discountPct: string
  /** Blank: the order's rate applies. */
  taxPct: string
}

export type PoDraftShipTo = Record<keyof PurchaseOrderAddress, string>

export interface PoDraft {
  supplierName: string
  supplierContact: string
  supplierAddress: string
  supplierPhone: string
  supplierEmail: string
  supplierTaxId: string
  requestedDeliveryDate: string
  paymentTerms: string
  incoterms: string
  shippingMethod: string
  currency: string
  buyer: string
  reference: string
  notes: string
  /** This order's own terms & conditions; null = the template's standard terms. */
  terms: string | null
  taxRatePct: string
  shippingCost: string
  shipTo: PoDraftShipTo
  /** The template's custom fields, by key, as typed. */
  customFields: Record<string, string>
  lines: PoDraftLine[]
}

/** The Incoterms 2020 rules, offered as suggestions; any other text is accepted. */
export const INCOTERMS = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FOB', 'CIF'] as const

export const DEFAULT_CURRENCY = 'EUR'

const SHIP_TO_KEYS: (keyof PurchaseOrderAddress)[] = [
  'name',
  'street',
  'postcode',
  'city',
  'country',
  'phone',
  'email',
]

let lineSeq = 0
function nextKey(): string {
  lineSeq += 1
  return `line-${lineSeq}`
}

const str = (v: string | null | undefined) => v ?? ''
const numStr = (v: number | null | undefined) => (v == null ? '' : String(v))

/** A typed figure as a number, or undefined when blank or not a number. */
export function draftNumber(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** `YYYY-MM-DD`, `days` after `from` (local calendar day). */
export function addDaysIso(from: Date, days: number): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

export function emptyDraftLine(materialId: string): PoDraftLine {
  return {
    key: nextKey(),
    materialId,
    articleNo: '',
    supplierRef: '',
    description: '',
    qty: '1',
    uom: '',
    unitPrice: '',
    discountPct: '',
    taxPct: '',
  }
}

export function emptyDraft(requestedDeliveryDate: string): PoDraft {
  return {
    supplierName: '',
    supplierContact: '',
    supplierAddress: '',
    supplierPhone: '',
    supplierEmail: '',
    supplierTaxId: '',
    requestedDeliveryDate,
    paymentTerms: '',
    incoterms: '',
    shippingMethod: '',
    currency: DEFAULT_CURRENCY,
    buyer: '',
    reference: '',
    notes: '',
    terms: null,
    taxRatePct: '',
    shippingCost: '',
    customFields: {},
    shipTo: Object.fromEntries(SHIP_TO_KEYS.map((k) => [k, ''])) as PoDraftShipTo,
    lines: [],
  }
}

/** The prefill's line, as an editable row. */
export function draftLineFromPrefill(line: PurchaseOrderPrefill['line']): PoDraftLine {
  return {
    ...emptyDraftLine(line.materialId),
    articleNo: str(line.articleNo),
    supplierRef: str(line.supplierRef),
    description: str(line.description),
    qty: numStr(line.qty),
    uom: str(line.uom),
    unitPrice: numStr(line.unitPrice),
  }
}

/**
 * A new order seeded from one material's prefill: the supplier, the line,
 * and the template's defaults the server puts on it. `fallbackDeliveryDate`
 * is the client's own today + lead time, used only when the server sends no
 * date of its own. The values shown are the values sent back, so a field the
 * user leaves alone carries the server's default rather than a blank.
 */
export function draftFromPrefill(prefill: PurchaseOrderPrefill, fallbackDeliveryDate: string): PoDraft {
  const base = emptyDraft(prefill.requestedDeliveryDate?.slice(0, 10) || fallbackDeliveryDate)
  return {
    ...base,
    supplierName: str(prefill.supplierName),
    supplierContact: str(prefill.supplierContact),
    supplierEmail: str(prefill.supplierEmail),
    supplierPhone: str(prefill.supplierPhone),
    currency: prefill.currency || DEFAULT_CURRENCY,
    taxRatePct: numStr(prefill.taxRatePct),
    paymentTerms: str(prefill.paymentTerms),
    incoterms: str(prefill.incoterms),
    notes: str(prefill.notes),
    shipTo: Object.fromEntries(SHIP_TO_KEYS.map((k) => [k, str(prefill.shipTo?.[k])])) as PoDraftShipTo,
    lines: [draftLineFromPrefill(prefill.line)],
  }
}

/** An existing order, ready to edit. */
export function draftFromDetail(po: PurchaseOrderDetail): PoDraft {
  return {
    supplierName: po.supplierName,
    supplierContact: str(po.supplierContact),
    supplierAddress: str(po.supplierAddress),
    supplierPhone: str(po.supplierPhone),
    supplierEmail: str(po.supplierEmail),
    supplierTaxId: str(po.supplierTaxId),
    requestedDeliveryDate: po.requestedDeliveryDate ? po.requestedDeliveryDate.slice(0, 10) : '',
    paymentTerms: str(po.paymentTerms),
    incoterms: str(po.incoterms),
    shippingMethod: str(po.shippingMethod),
    currency: po.currency || DEFAULT_CURRENCY,
    buyer: str(po.buyer),
    reference: str(po.reference),
    notes: str(po.notes),
    terms: po.terms ?? null,
    taxRatePct: numStr(po.taxRatePct),
    shippingCost: numStr(po.shippingCost),
    customFields: Object.fromEntries(
      Object.entries(po.customFields ?? {}).map(([k, v]) => [k, v == null ? '' : String(v)]),
    ),
    shipTo: Object.fromEntries(SHIP_TO_KEYS.map((k) => [k, str(po.shipTo?.[k])])) as PoDraftShipTo,
    lines: po.lines.map((l) => ({
      key: nextKey(),
      materialId: l.materialId,
      articleNo: str(l.articleNo),
      supplierRef: str(l.supplierRef),
      description: str(l.description),
      qty: String(l.qty),
      uom: str(l.uom),
      unitPrice: String(l.unitPrice),
      discountPct: l.discountPct ? String(l.discountPct) : '',
      taxPct: numStr(l.taxPct),
    })),
  }
}

/** Live totals for the footer. Blank figures count as zero. */
export function draftTotals(draft: PoDraft): PoTotals {
  return computePoTotals(
    draft.lines.map((l) => ({
      qty: draftNumber(l.qty) ?? 0,
      unitPrice: draftNumber(l.unitPrice) ?? 0,
      discountPct: draftNumber(l.discountPct) ?? 0,
      taxPct: draftNumber(l.taxPct) ?? null,
    })),
    { taxRatePct: draftNumber(draft.taxRatePct) ?? 0, shippingCost: draftNumber(draft.shippingCost) ?? 0 },
  )
}

/** What stops the draft being saved, or null when it can be. */
export function draftProblem(draft: PoDraft): string | null {
  if (!draft.supplierName.trim()) return 'Enter the supplier.'
  if (draft.lines.length === 0) return 'Add at least one line.'
  for (const [i, l] of draft.lines.entries()) {
    const qty = draftNumber(l.qty)
    if (qty === undefined || qty <= 0) return `Line ${i + 1}: enter a quantity above zero.`
    const price = draftNumber(l.unitPrice)
    if (price !== undefined && price < 0) return `Line ${i + 1}: the unit price cannot be negative.`
    const discount = draftNumber(l.discountPct)
    if (discount !== undefined && (discount < 0 || discount > 100)) {
      return `Line ${i + 1}: the discount must be between 0 and 100%.`
    }
  }
  return null
}

const opt = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim())

function lineToInput(l: PoDraftLine): PurchaseOrderLineInput {
  return {
    materialId: l.materialId,
    qty: draftNumber(l.qty) ?? 0,
    unitPrice: draftNumber(l.unitPrice),
    discountPct: draftNumber(l.discountPct),
    taxPct: draftNumber(l.taxPct),
    description: opt(l.description),
    supplierRef: opt(l.supplierRef),
    uom: opt(l.uom),
  }
}

/**
 * The body for create (and, with `reason`, update). Blank text is left out
 * rather than sent as "", so an untouched field stays unset on the server.
 */
export function draftToPayload(draft: PoDraft, customFieldDefs: PoCustomFieldDef[] = []): CreatePurchaseOrderPayload {
  const shipToEntries = SHIP_TO_KEYS.map((k) => [k, opt(draft.shipTo[k])] as const).filter(([, v]) => v)
  // Every key the template defines, plus any the order already carries from
  // an earlier template, so editing never drops a stored value. Blank is null.
  const typeOf = new Map(customFieldDefs.map((d) => [d.key, d.type]))
  const customKeys = [...new Set([...customFieldDefs.map((d) => d.key), ...Object.keys(draft.customFields)])]
  const customFields: PoCustomFieldValues = Object.fromEntries(
    customKeys.map((k) => {
      const raw = draft.customFields[k] ?? ''
      return [k, raw.trim() === '' ? null : typeOf.get(k) === 'number' ? (draftNumber(raw) ?? null) : raw.trim()]
    }),
  )
  return {
    supplierName: draft.supplierName.trim(),
    supplierContact: opt(draft.supplierContact),
    supplierAddress: opt(draft.supplierAddress),
    supplierPhone: opt(draft.supplierPhone),
    supplierEmail: opt(draft.supplierEmail),
    supplierTaxId: opt(draft.supplierTaxId),
    shipTo: shipToEntries.length > 0 ? Object.fromEntries(shipToEntries) : undefined,
    requestedDeliveryDate: opt(draft.requestedDeliveryDate),
    paymentTerms: opt(draft.paymentTerms),
    incoterms: opt(draft.incoterms),
    shippingMethod: opt(draft.shippingMethod),
    currency: opt(draft.currency),
    buyer: opt(draft.buyer),
    reference: opt(draft.reference),
    notes: opt(draft.notes),
    // Null is sent on purpose: on an update it switches back to the standard terms.
    terms: draft.terms !== null && draft.terms.trim() !== '' ? draft.terms : null,
    taxRatePct: draftNumber(draft.taxRatePct),
    shippingCost: draftNumber(draft.shippingCost),
    customFields: customKeys.length > 0 ? customFields : undefined,
    lines: draft.lines.map(lineToInput),
  }
}

/** The prefill's line as an add-line body, for adding a material to an existing draft. */
export function prefillLineInput(line: PurchaseOrderPrefill['line']): PurchaseOrderLineInput {
  return lineToInput(draftLineFromPrefill(line))
}
